// Utility to generate a full HTML document string from content and optional styles/title

import DOMPurify from "dompurify";
import { HtmlTemplateOptions } from "../Interfaces/AIPlayground.interfaces";

/**
 * Builds a complete HTML document string with the provided content, styles, and title.
 * Sanitizes the content to prevent XSS attacks and ensures images are resized appropriately.
 *
 * @param {string | undefined} htmlContent - The HTML content to include in the document.
 * @param {HtmlTemplateOptions} [options] - Optional styles and title for the document.
 * @returns {string | null} - The complete HTML document string or null if no content is provided.
 */
export const buildHtmlDocument = (htmlContent: string | undefined, options?: HtmlTemplateOptions): string | null => {
  if (!htmlContent) return null;

  /**
   * Default styles applied to the HTML document.
   * These styles ensure a consistent look and feel across the document,
   * including font smoothing, font family, and basic table styling.
   */
  const defaultStyles = `
    body {
      -moz-osx-font-smoothing: grayscale;
      -webkit-font-smoothing: antialiased;
      font-family: "Roboto", sans-serif !important;
      margin: 0;
    }
    h2 {
      color: rgb(0, 120, 212) !important;
      font-family: "Roboto", sans-serif;
      font-size: 18px !important;
      font-weight: 700 !important;
    }
    h3 {
      display: block;
      font-size: 1.17em;
      font-weight: bold;
      margin-block-end: 1em;
      margin-block-start: 1em;
      margin-inline-end: 0px;
      margin-inline-start: 0px;
      unicode-bidi: isolate;
    }
    table, th, tr, td {
      border-collapse: collapse;
      border: 1px solid rgb(189, 185, 185);
      padding: 2px;
    }
  `;

  // The following lines sanitize the styles and title to prevent XSS attacks or injection of unwanted HTML.
  // DOMPurify is used to remove all tags and attributes, ensuring only plain text is used for styles and title.
  // This is important because styles and titles are injected directly into the HTML document and could otherwise be exploited.
  // Sanitize title and styles before use
  const styles = DOMPurify.sanitize(options?.styles ?? defaultStyles, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  const title = DOMPurify.sanitize(options?.title ?? "Document", { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

  const resizeImagesInHtml = (html: string) => {
    // Sanitize the provided HTML content to remove any potentially malicious scripts or markup.
    // This helps prevent XSS attacks by ensuring only safe HTML is processed further.
    const sanitizedHtml = DOMPurify.sanitize(html);
    // Create a temporary div element to manipulate the HTML content.
    // This allows us to safely modify the HTML without affecting the original content.
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = sanitizedHtml;
    tempDiv.querySelectorAll("img").forEach((img) => {
      img.setAttribute("style", "max-width:600px;height:auto;");
      img.setAttribute("width", "600");
      img.removeAttribute("height");
    });
    // Return the modified HTML content with resized images.
    // This ensures that all images in the HTML content are resized to a maximum width of 600px,
    // maintaining their aspect ratio by setting the height to auto.
    return tempDiv.innerHTML;
  };

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>${title}</title>
        <style>${styles}</style>
      </head>
      <body>${resizeImagesInHtml(htmlContent)}</body>
    </html>
  `;
};
